import angular from 'angular';
import uiRouter from 'angular-ui-router';

/**
 * A specialized version of `_.some` for arrays without support for callback
 * shorthands and `this` binding.
 *
 * @private
 * @param {Array} array The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

var arraySome_1 = arraySome;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Function} [customizer] The function to customize comparing arrays.
 * @param {boolean} [isLoose] Specify performing partial comparisons.
 * @param {Array} [stackA] Tracks traversed `value` objects.
 * @param {Array} [stackB] Tracks traversed `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, equalFunc, customizer, isLoose, stackA, stackB) {
  var index = -1,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isLoose && othLength > arrLength)) {
    return false;
  }
  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index],
        result = customizer ? customizer(isLoose ? othValue : arrValue, isLoose ? arrValue : othValue, index) : undefined;

    if (result !== undefined) {
      if (result) {
        continue;
      }
      return false;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (isLoose) {
      if (!arraySome_1(other, function(othValue) {
            return arrValue === othValue || equalFunc(arrValue, othValue, customizer, isLoose, stackA, stackB);
          })) {
        return false;
      }
    } else if (!(arrValue === othValue || equalFunc(arrValue, othValue, customizer, isLoose, stackA, stackB))) {
      return false;
    }
  }
  return true;
}

var equalArrays_1 = equalArrays;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]';
var dateTag = '[object Date]';
var errorTag = '[object Error]';
var numberTag = '[object Number]';
var regexpTag = '[object RegExp]';
var stringTag = '[object String]';

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag) {
  switch (tag) {
    case boolTag:
    case dateTag:
      // Coerce dates and booleans to numbers, dates to milliseconds and booleans
      // to `1` or `0` treating invalid dates coerced to `NaN` as not equal.
      return +object == +other;

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case numberTag:
      // Treat `NaN` vs. `NaN` as equal.
      return (object != +object)
        ? other != +other
        : object == +other;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings primitives and string
      // objects as equal. See https://es5.github.io/#x15.10.6.4 for more details.
      return object == (other + '');
  }
  return false;
}

var equalByTag_1 = equalByTag;

/**
 * Checks if `value` is the [language type](https://es5.github.io/#x8) of `Object`.
 * (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(1);
 * // => false
 */
function isObject(value) {
  // Avoid a V8 JIT bug in Chrome 19-20.
  // See https://code.google.com/p/v8/issues/detail?id=2291 for more details.
  var type = typeof value;
  return !!value && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var funcTag = '[object Function]';

/** Used for native method references. */
var objectProto$3 = Object.prototype;

/**
 * Used to resolve the [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objToString$1 = objectProto$3.toString;

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is correctly classified, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in older versions of Chrome and Safari which return 'function' for regexes
  // and Safari 8 which returns 'object' for typed array constructors.
  return isObject_1(value) && objToString$1.call(value) == funcTag;
}

var isFunction_1 = isFunction;

/**
 * Checks if `value` is object-like.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** Used to detect host constructors (Safari > 5). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for native method references. */
var objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var fnToString = Function.prototype.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  fnToString.call(hasOwnProperty$2).replace(/[\\^$.*+?()[\]{}|]/g, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * Checks if `value` is a native function.
 *
 * @static
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function, else `false`.
 * @example
 *
 * _.isNative(Array.prototype.push);
 * // => true
 *
 * _.isNative(_);
 * // => false
 */
function isNative(value) {
  if (value == null) {
    return false;
  }
  if (isFunction_1(value)) {
    return reIsNative.test(fnToString.call(value));
  }
  return isObjectLike_1(value) && reIsHostCtor.test(value);
}

var isNative_1 = isNative;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = object == null ? undefined : object[key];
  return isNative_1(value) ? value : undefined;
}

var getNative_1 = getNative;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

var baseProperty_1 = baseProperty;

/**
 * Gets the "length" property value of `object`.
 *
 * **Note:** This function is used to avoid a [JIT bug](https://bugs.webkit.org/show_bug.cgi?id=142792)
 * that affects Safari on at least iOS 8.1-8.3 ARM64.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {*} Returns the "length" value.
 */
var getLength = baseProperty_1('length');

var getLength_1 = getLength;

/**
 * Used as the [maximum length](http://ecma-international.org/ecma-262/6.0/#sec-number.max_safe_integer)
 * of an array-like value.
 */
var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This function is based on [`ToLength`](http://ecma-international.org/ecma-262/6.0/#sec-tolength).
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 */
function isLength(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

var isLength_1 = isLength;

/**
 * Checks if `value` is array-like.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 */
function isArrayLike(value) {
  return value != null && isLength_1(getLength_1(value));
}

var isArrayLike_1 = isArrayLike;

/** Used for native method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/** Native method references. */
var propertyIsEnumerable = objectProto$5.propertyIsEnumerable;

/**
 * Checks if `value` is classified as an `arguments` object.
 *
 * @static
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is correctly classified, else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
function isArguments(value) {
  return isObjectLike_1(value) && isArrayLike_1(value) &&
    hasOwnProperty$4.call(value, 'callee') && !propertyIsEnumerable.call(value, 'callee');
}

var isArguments_1 = isArguments;

/** `Object#toString` result references. */
var arrayTag$1 = '[object Array]';

/** Used for native method references. */
var objectProto$6 = Object.prototype;

/**
 * Used to resolve the [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objToString$2 = objectProto$6.toString;

/* Native method references for those with the same name as other `lodash` methods. */
var nativeIsArray = getNative_1(Array, 'isArray');

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is correctly classified, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(function() { return arguments; }());
 * // => false
 */
var isArray = nativeIsArray || function(value) {
  return isObjectLike_1(value) && isLength_1(value.length) && objToString$2.call(value) == arrayTag$1;
};

var isArray_1 = isArray;

/** Used to detect unsigned integer values. */
var reIsUint = /^\d+$/;

/**
 * Used as the [maximum length](http://ecma-international.org/ecma-262/6.0/#sec-number.max_safe_integer)
 * of an array-like value.
 */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  value = (typeof value == 'number' || reIsUint.test(value)) ? +value : -1;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;
  return value > -1 && value % 1 == 0 && value < length;
}

var isIndex_1 = isIndex;

/** Used for native method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  if (object == null) {
    return [];
  }
  if (!isObject_1(object)) {
    object = Object(object);
  }
  var length = object.length;
  length = (length && isLength_1(length) &&
    (isArray_1(object) || isArguments_1(object)) && length) || 0;

  var Ctor = object.constructor,
      index = -1,
      isProto = typeof Ctor == 'function' && Ctor.prototype === object,
      result = Array(length),
      skipIndexes = length > 0;

  while (++index < length) {
    result[index] = (index + '');
  }
  for (var key in object) {
    if (!(skipIndexes && isIndex_1(key, length)) &&
        !(key == 'constructor' && (isProto || !hasOwnProperty$5.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var keysIn_1 = keysIn;

/** Used for native method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * A fallback implementation of `Object.keys` which creates an array of the
 * own enumerable property names of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function shimKeys(object) {
  var props = keysIn_1(object),
      propsLength = props.length,
      length = propsLength && object.length;

  var allowIndexes = !!length && isLength_1(length) &&
    (isArray_1(object) || isArguments_1(object));

  var index = -1,
      result = [];

  while (++index < propsLength) {
    var key = props[index];
    if ((allowIndexes && isIndex_1(key, length)) || hasOwnProperty$3.call(object, key)) {
      result.push(key);
    }
  }
  return result;
}

var shimKeys_1 = shimKeys;

/* Native method references for those with the same name as other `lodash` methods. */
var nativeKeys = getNative_1(Object, 'keys');

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/6.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
var keys = !nativeKeys ? shimKeys_1 : function(object) {
  var Ctor = object == null ? undefined : object.constructor;
  if ((typeof Ctor == 'function' && Ctor.prototype === object) ||
      (typeof object != 'function' && isArrayLike_1(object))) {
    return shimKeys_1(object);
  }
  return isObject_1(object) ? nativeKeys(object) : [];
};

var keys_1 = keys;

/** Used for native method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Function} [customizer] The function to customize comparing values.
 * @param {boolean} [isLoose] Specify performing partial comparisons.
 * @param {Array} [stackA] Tracks traversed `value` objects.
 * @param {Array} [stackB] Tracks traversed `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, equalFunc, customizer, isLoose, stackA, stackB) {
  var objProps = keys_1(object),
      objLength = objProps.length,
      othProps = keys_1(other),
      othLength = othProps.length;

  if (objLength != othLength && !isLoose) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isLoose ? key in other : hasOwnProperty$1.call(other, key))) {
      return false;
    }
  }
  var skipCtor = isLoose;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key],
        result = customizer ? customizer(isLoose ? othValue : objValue, isLoose? objValue : othValue, key) : undefined;

    // Recursively compare objects (susceptible to call stack limits).
    if (!(result === undefined ? equalFunc(objValue, othValue, customizer, isLoose, stackA, stackB) : result)) {
      return false;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (!skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      return false;
    }
  }
  return true;
}

var equalObjects_1 = equalObjects;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]';
var arrayTag$2 = '[object Array]';
var boolTag$1 = '[object Boolean]';
var dateTag$1 = '[object Date]';
var errorTag$1 = '[object Error]';
var funcTag$1 = '[object Function]';
var mapTag = '[object Map]';
var numberTag$1 = '[object Number]';
var objectTag$1 = '[object Object]';
var regexpTag$1 = '[object RegExp]';
var setTag = '[object Set]';
var stringTag$1 = '[object String]';
var weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]';
var float32Tag = '[object Float32Array]';
var float64Tag = '[object Float64Array]';
var int8Tag = '[object Int8Array]';
var int16Tag = '[object Int16Array]';
var int32Tag = '[object Int32Array]';
var uint8Tag = '[object Uint8Array]';
var uint8ClampedTag = '[object Uint8ClampedArray]';
var uint16Tag = '[object Uint16Array]';
var uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag$2] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag$1] =
typedArrayTags[dateTag$1] = typedArrayTags[errorTag$1] =
typedArrayTags[funcTag$1] = typedArrayTags[mapTag] =
typedArrayTags[numberTag$1] = typedArrayTags[objectTag$1] =
typedArrayTags[regexpTag$1] = typedArrayTags[setTag] =
typedArrayTags[stringTag$1] = typedArrayTags[weakMapTag] = false;

/** Used for native method references. */
var objectProto$8 = Object.prototype;

/**
 * Used to resolve the [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objToString$3 = objectProto$8.toString;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is correctly classified, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
function isTypedArray(value) {
  return isObjectLike_1(value) && isLength_1(value.length) && !!typedArrayTags[objToString$3.call(value)];
}

var isTypedArray_1 = isTypedArray;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';
var arrayTag = '[object Array]';
var objectTag = '[object Object]';

/** Used for native method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the [`toStringTag`](http://ecma-international.org/ecma-262/6.0/#sec-object.prototype.tostring)
 * of values.
 */
var objToString = objectProto.toString;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Function} [customizer] The function to customize comparing objects.
 * @param {boolean} [isLoose] Specify performing partial comparisons.
 * @param {Array} [stackA=[]] Tracks traversed `value` objects.
 * @param {Array} [stackB=[]] Tracks traversed `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, equalFunc, customizer, isLoose, stackA, stackB) {
  var objIsArr = isArray_1(object),
      othIsArr = isArray_1(other),
      objTag = arrayTag,
      othTag = arrayTag;

  if (!objIsArr) {
    objTag = objToString.call(object);
    if (objTag == argsTag) {
      objTag = objectTag;
    } else if (objTag != objectTag) {
      objIsArr = isTypedArray_1(object);
    }
  }
  if (!othIsArr) {
    othTag = objToString.call(other);
    if (othTag == argsTag) {
      othTag = objectTag;
    } else if (othTag != objectTag) {
      othIsArr = isTypedArray_1(other);
    }
  }
  var objIsObj = objTag == objectTag,
      othIsObj = othTag == objectTag,
      isSameTag = objTag == othTag;

  if (isSameTag && !(objIsArr || objIsObj)) {
    return equalByTag_1(object, other, objTag);
  }
  if (!isLoose) {
    var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      return equalFunc(objIsWrapped ? object.value() : object, othIsWrapped ? other.value() : other, customizer, isLoose, stackA, stackB);
    }
  }
  if (!isSameTag) {
    return false;
  }
  // Assume cyclic values are equal.
  // For more information on detecting circular references see https://es5.github.io/#JO.
  stackA || (stackA = []);
  stackB || (stackB = []);

  var length = stackA.length;
  while (length--) {
    if (stackA[length] == object) {
      return stackB[length] == other;
    }
  }
  // Add `object` and `other` to the stack of traversed objects.
  stackA.push(object);
  stackB.push(other);

  var result = (objIsArr ? equalArrays_1 : equalObjects_1)(object, other, equalFunc, customizer, isLoose, stackA, stackB);

  stackA.pop();
  stackB.pop();

  return result;
}

var baseIsEqualDeep_1 = baseIsEqualDeep;

/**
 * The base implementation of `_.isEqual` without support for `this` binding
 * `customizer` functions.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {Function} [customizer] The function to customize comparing values.
 * @param {boolean} [isLoose] Specify performing partial comparisons.
 * @param {Array} [stackA] Tracks traversed `value` objects.
 * @param {Array} [stackB] Tracks traversed `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, customizer, isLoose, stackA, stackB) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObject_1(value) && !isObjectLike_1(other))) {
    return value !== value && other !== other;
  }
  return baseIsEqualDeep_1(value, other, baseIsEqual, customizer, isLoose, stackA, stackB);
}

var baseIsEqual_1 = baseIsEqual;

/**
 * Converts `value` to an object if it's not one.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {Object} Returns the object.
 */
function toObject(value) {
  return isObject_1(value) ? value : Object(value);
}

var toObject_1 = toObject;

/**
 * The base implementation of `_.isMatch` without support for callback
 * shorthands and `this` binding.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Array} matchData The propery names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparing objects.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = toObject_1(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var result = customizer ? customizer(objValue, srcValue, key) : undefined;
      if (!(result === undefined ? baseIsEqual_1(srcValue, objValue, customizer, true) : result)) {
        return false;
      }
    }
  }
  return true;
}

var baseIsMatch_1 = baseIsMatch;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject_1(value);
}

var isStrictComparable_1 = isStrictComparable;

/**
 * Creates a two dimensional array of the key-value pairs for `object`,
 * e.g. `[[key1, value1], [key2, value2]]`.
 *
 * @static
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the new array of key-value pairs.
 * @example
 *
 * _.pairs({ 'barney': 36, 'fred': 40 });
 * // => [['barney', 36], ['fred', 40]] (iteration order is not guaranteed)
 */
function pairs(object) {
  object = toObject_1(object);

  var index = -1,
      props = keys_1(object),
      length = props.length,
      result = Array(length);

  while (++index < length) {
    var key = props[index];
    result[index] = [key, object[key]];
  }
  return result;
}

var pairs_1 = pairs;

/**
 * Gets the propery names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = pairs_1(object),
      length = result.length;

  while (length--) {
    result[length][2] = isStrictComparable_1(result[length][1]);
  }
  return result;
}

var getMatchData_1 = getMatchData;

/**
 * The base implementation of `_.matches` which does not clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new function.
 */
function baseMatches(source) {
  var matchData = getMatchData_1(source);
  if (matchData.length == 1 && matchData[0][2]) {
    var key = matchData[0][0],
        value = matchData[0][1];

    return function(object) {
      if (object == null) {
        return false;
      }
      return object[key] === value && (value !== undefined || (key in toObject_1(object)));
    };
  }
  return function(object) {
    return baseIsMatch_1(object, matchData);
  };
}

var baseMatches_1 = baseMatches;

/**
 * The base implementation of `get` without support for string paths
 * and default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} path The path of the property to get.
 * @param {string} [pathKey] The key representation of path.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path, pathKey) {
  if (object == null) {
    return;
  }
  if (pathKey !== undefined && pathKey in toObject_1(object)) {
    path = [pathKey];
  }
  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[path[index++]];
  }
  return (index && index == length) ? object : undefined;
}

var baseGet_1 = baseGet;

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */
function baseSlice(array, start, end) {
  var index = -1,
      length = array.length;

  start = start == null ? 0 : (+start || 0);
  if (start < 0) {
    start = -start > length ? 0 : (length + start);
  }
  end = (end === undefined || end > length) ? length : (+end || 0);
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : ((end - start) >>> 0);
  start >>>= 0;

  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}

var baseSlice_1 = baseSlice;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\n\\]|\\.)*?\1)\]/;
var reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  var type = typeof value;
  if ((type == 'string' && reIsPlainProp.test(value)) || type == 'number') {
    return true;
  }
  if (isArray_1(value)) {
    return false;
  }
  var result = !reIsDeepProp.test(value);
  return result || (object != null && value in toObject_1(object));
}

var isKey_1 = isKey;

/**
 * Gets the last element of `array`.
 *
 * @static
 * @memberOf _
 * @category Array
 * @param {Array} array The array to query.
 * @returns {*} Returns the last element of `array`.
 * @example
 *
 * _.last([1, 2, 3]);
 * // => 3
 */
function last(array) {
  var length = array ? array.length : 0;
  return length ? array[length - 1] : undefined;
}

var last_1 = last;

/**
 * Converts `value` to a string if it's not one. An empty string is returned
 * for `null` or `undefined` values.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  return value == null ? '' : (value + '');
}

var baseToString_1 = baseToString;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\n\\]|\\.)*?)\2)\]/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `value` to property path array if it's not one.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {Array} Returns the property path array.
 */
function toPath(value) {
  if (isArray_1(value)) {
    return value;
  }
  var result = [];
  baseToString_1(value).replace(rePropName, function(match, number, quote, string) {
    result.push(quote ? string.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
}

var toPath_1 = toPath;

/**
 * The base implementation of `_.matchesProperty` which does not clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to compare.
 * @returns {Function} Returns the new function.
 */
function baseMatchesProperty(path, srcValue) {
  var isArr = isArray_1(path),
      isCommon = isKey_1(path) && isStrictComparable_1(srcValue),
      pathKey = (path + '');

  path = toPath_1(path);
  return function(object) {
    if (object == null) {
      return false;
    }
    var key = pathKey;
    object = toObject_1(object);
    if ((isArr || !isCommon) && !(key in object)) {
      object = path.length == 1 ? object : baseGet_1(object, baseSlice_1(path, 0, -1));
      if (object == null) {
        return false;
      }
      key = last_1(path);
      object = toObject_1(object);
    }
    return object[key] === srcValue
      ? (srcValue !== undefined || (key in object))
      : baseIsEqual_1(srcValue, object[key], undefined, true);
  };
}

var baseMatchesProperty_1 = baseMatchesProperty;

/**
 * This method returns the first argument provided to it.
 *
 * @static
 * @memberOf _
 * @category Utility
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'user': 'fred' };
 *
 * _.identity(object) === object;
 * // => true
 */
function identity(value) {
  return value;
}

var identity_1 = identity;

/**
 * A specialized version of `baseCallback` which only supports `this` binding
 * and specifying the number of arguments to provide to `func`.
 *
 * @private
 * @param {Function} func The function to bind.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {number} [argCount] The number of arguments to provide to `func`.
 * @returns {Function} Returns the callback.
 */
function bindCallback(func, thisArg, argCount) {
  if (typeof func != 'function') {
    return identity_1;
  }
  if (thisArg === undefined) {
    return func;
  }
  switch (argCount) {
    case 1: return function(value) {
      return func.call(thisArg, value);
    };
    case 3: return function(value, index, collection) {
      return func.call(thisArg, value, index, collection);
    };
    case 4: return function(accumulator, value, index, collection) {
      return func.call(thisArg, accumulator, value, index, collection);
    };
    case 5: return function(value, other, key, object, source) {
      return func.call(thisArg, value, other, key, object, source);
    };
  }
  return function() {
    return func.apply(thisArg, arguments);
  };
}

var bindCallback_1 = bindCallback;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new function.
 */
function basePropertyDeep(path) {
  var pathKey = (path + '');
  path = toPath_1(path);
  return function(object) {
    return baseGet_1(object, path, pathKey);
  };
}

var basePropertyDeep_1 = basePropertyDeep;

/**
 * Creates a function that returns the property value at `path` on a
 * given object.
 *
 * @static
 * @memberOf _
 * @category Utility
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': { 'c': 2 } } },
 *   { 'a': { 'b': { 'c': 1 } } }
 * ];
 *
 * _.map(objects, _.property('a.b.c'));
 * // => [2, 1]
 *
 * _.pluck(_.sortBy(objects, _.property(['a', 'b', 'c'])), 'a.b.c');
 * // => [1, 2]
 */
function property(path) {
  return isKey_1(path) ? baseProperty_1(path) : basePropertyDeep_1(path);
}

var property_1 = property;

/**
 * The base implementation of `_.callback` which supports specifying the
 * number of arguments to provide to `func`.
 *
 * @private
 * @param {*} [func=_.identity] The value to convert to a callback.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {number} [argCount] The number of arguments to provide to `func`.
 * @returns {Function} Returns the callback.
 */
function baseCallback(func, thisArg, argCount) {
  var type = typeof func;
  if (type == 'function') {
    return thisArg === undefined
      ? func
      : bindCallback_1(func, thisArg, argCount);
  }
  if (func == null) {
    return identity_1;
  }
  if (type == 'object') {
    return baseMatches_1(func);
  }
  return thisArg === undefined
    ? property_1(func)
    : baseMatchesProperty_1(func, thisArg);
}

var baseCallback_1 = baseCallback;

/**
 * Gets the index at which the first occurrence of `NaN` is found in `array`.
 *
 * @private
 * @param {Array} array The array to search.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched `NaN`, else `-1`.
 */
function indexOfNaN(array, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 0 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    var other = array[index];
    if (other !== other) {
      return index;
    }
  }
  return -1;
}

var indexOfNaN_1 = indexOfNaN;

/**
 * The base implementation of `_.indexOf` without support for binary searches.
 *
 * @private
 * @param {Array} array The array to search.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf(array, value, fromIndex) {
  if (value !== value) {
    return indexOfNaN_1(array, fromIndex);
  }
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

var baseIndexOf_1 = baseIndexOf;

/**
 * Checks if `value` is in `cache` mimicking the return signature of
 * `_.indexOf` by returning `0` if the value is found, else `-1`.
 *
 * @private
 * @param {Object} cache The cache to search.
 * @param {*} value The value to search for.
 * @returns {number} Returns `0` if `value` is found, else `-1`.
 */
function cacheIndexOf(cache, value) {
  var data = cache.data,
      result = (typeof value == 'string' || isObject_1(value)) ? data.set.has(value) : data.hash[value];

  return result ? 0 : -1;
}

var cacheIndexOf_1 = cacheIndexOf;

var commonjsGlobal = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/**
 * Adds `value` to the cache.
 *
 * @private
 * @name push
 * @memberOf SetCache
 * @param {*} value The value to cache.
 */
function cachePush(value) {
  var data = this.data;
  if (typeof value == 'string' || isObject_1(value)) {
    data.set.add(value);
  } else {
    data.hash[value] = true;
  }
}

var cachePush_1 = cachePush;

/** Native method references. */
var Set$1 = getNative_1(commonjsGlobal, 'Set');

/* Native method references for those with the same name as other `lodash` methods. */
var nativeCreate$1 = getNative_1(Object, 'create');

/**
 *
 * Creates a cache object to store unique values.
 *
 * @private
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var length = values ? values.length : 0;

  this.data = { 'hash': nativeCreate$1(null), 'set': new Set$1 };
  while (length--) {
    this.push(values[length]);
  }
}

// Add functions to the `Set` cache.
SetCache.prototype.push = cachePush_1;

var SetCache_1 = SetCache;

/** Native method references. */
var Set = getNative_1(commonjsGlobal, 'Set');

/* Native method references for those with the same name as other `lodash` methods. */
var nativeCreate = getNative_1(Object, 'create');

/**
 * Creates a `Set` cache object to optimize linear searches of large arrays.
 *
 * @private
 * @param {Array} [values] The values to cache.
 * @returns {null|Object} Returns the new cache object if `Set` is supported, else `null`.
 */
function createCache(values) {
  return (nativeCreate && Set) ? new SetCache_1(values) : null;
}

var createCache_1 = createCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * The base implementation of `_.uniq` without support for callback shorthands
 * and `this` binding.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee] The function invoked per iteration.
 * @returns {Array} Returns the new duplicate free array.
 */
function baseUniq(array, iteratee) {
  var index = -1,
      indexOf = baseIndexOf_1,
      length = array.length,
      isCommon = true,
      isLarge = isCommon && length >= LARGE_ARRAY_SIZE,
      seen = isLarge ? createCache_1() : null,
      result = [];

  if (seen) {
    indexOf = cacheIndexOf_1;
    isCommon = false;
  } else {
    isLarge = false;
    seen = iteratee ? [] : result;
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee ? iteratee(value, index, array) : value;

    if (isCommon && value === value) {
      var seenIndex = seen.length;
      while (seenIndex--) {
        if (seen[seenIndex] === computed) {
          continue outer;
        }
      }
      if (iteratee) {
        seen.push(computed);
      }
      result.push(value);
    }
    else if (indexOf(seen, computed, 0) < 0) {
      if (iteratee || isLarge) {
        seen.push(computed);
      }
      result.push(value);
    }
  }
  return result;
}

var baseUniq_1 = baseUniq;

/**
 * Checks if the provided arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call, else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject_1(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
      ? (isArrayLike_1(object) && isIndex_1(index, object.length))
      : (type == 'string' && index in object)) {
    var other = object[index];
    return value === value ? (value === other) : (other !== other);
  }
  return false;
}

var isIterateeCall_1 = isIterateeCall;

/**
 * An implementation of `_.uniq` optimized for sorted arrays without support
 * for callback shorthands and `this` binding.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee] The function invoked per iteration.
 * @returns {Array} Returns the new duplicate free array.
 */
function sortedUniq(array, iteratee) {
  var seen,
      index = -1,
      length = array.length,
      resIndex = -1,
      result = [];

  while (++index < length) {
    var value = array[index],
        computed = iteratee ? iteratee(value, index, array) : value;

    if (!index || seen !== computed) {
      seen = computed;
      result[++resIndex] = value;
    }
  }
  return result;
}

var sortedUniq_1 = sortedUniq;

/**
 * Creates a duplicate-free version of an array, using
 * [`SameValueZero`](http://ecma-international.org/ecma-262/6.0/#sec-samevaluezero)
 * for equality comparisons, in which only the first occurence of each element
 * is kept. Providing `true` for `isSorted` performs a faster search algorithm
 * for sorted arrays. If an iteratee function is provided it's invoked for
 * each element in the array to generate the criterion by which uniqueness
 * is computed. The `iteratee` is bound to `thisArg` and invoked with three
 * arguments: (value, index, array).
 *
 * If a property name is provided for `iteratee` the created `_.property`
 * style callback returns the property value of the given element.
 *
 * If a value is also provided for `thisArg` the created `_.matchesProperty`
 * style callback returns `true` for elements that have a matching property
 * value, else `false`.
 *
 * If an object is provided for `iteratee` the created `_.matches` style
 * callback returns `true` for elements that have the properties of the given
 * object, else `false`.
 *
 * @static
 * @memberOf _
 * @alias unique
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {boolean} [isSorted] Specify the array is sorted.
 * @param {Function|Object|string} [iteratee] The function invoked per iteration.
 * @param {*} [thisArg] The `this` binding of `iteratee`.
 * @returns {Array} Returns the new duplicate-value-free array.
 * @example
 *
 * _.uniq([2, 1, 2]);
 * // => [2, 1]
 *
 * // using `isSorted`
 * _.uniq([1, 1, 2], true);
 * // => [1, 2]
 *
 * // using an iteratee function
 * _.uniq([1, 2.5, 1.5, 2], function(n) {
 *   return this.floor(n);
 * }, Math);
 * // => [1, 2.5]
 *
 * // using the `_.property` callback shorthand
 * _.uniq([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
 * // => [{ 'x': 1 }, { 'x': 2 }]
 */
function uniq(array, isSorted, iteratee, thisArg) {
  var length = array ? array.length : 0;
  if (!length) {
    return [];
  }
  if (isSorted != null && typeof isSorted != 'boolean') {
    thisArg = iteratee;
    iteratee = isIterateeCall_1(array, isSorted, thisArg) ? undefined : isSorted;
    isSorted = false;
  }
  iteratee = iteratee == null ? iteratee : baseCallback_1(iteratee, thisArg, 3);
  return (isSorted)
    ? sortedUniq_1(array, iteratee)
    : baseUniq_1(array, iteratee);
}

var uniq_1 = uniq;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var arrayPush_1 = arrayPush;

/**
 * The base implementation of `_.flatten` with added support for restricting
 * flattening and specifying the start index.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {boolean} [isDeep] Specify a deep flatten.
 * @param {boolean} [isStrict] Restrict flattening to arrays-like objects.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten(array, isDeep, isStrict, result) {
  result || (result = []);

  var index = -1,
      length = array.length;

  while (++index < length) {
    var value = array[index];
    if (isObjectLike_1(value) && isArrayLike_1(value) &&
        (isStrict || isArray_1(value) || isArguments_1(value))) {
      if (isDeep) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten(value, isDeep, isStrict, result);
      } else {
        arrayPush_1(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

var baseFlatten_1 = baseFlatten;

/**
 * Flattens a nested array. If `isDeep` is `true` the array is recursively
 * flattened, otherwise it's only flattened a single level.
 *
 * @static
 * @memberOf _
 * @category Array
 * @param {Array} array The array to flatten.
 * @param {boolean} [isDeep] Specify a deep flatten.
 * @param- {Object} [guard] Enables use as a callback for functions like `_.map`.
 * @returns {Array} Returns the new flattened array.
 * @example
 *
 * _.flatten([1, [2, 3, [4]]]);
 * // => [1, 2, 3, [4]]
 *
 * // using `isDeep`
 * _.flatten([1, [2, 3, [4]]], true);
 * // => [1, 2, 3, 4]
 */
function flatten(array, isDeep, guard) {
  var length = array ? array.length : 0;
  if (guard && isIterateeCall_1(array, isDeep, guard)) {
    isDeep = false;
  }
  return length ? baseFlatten_1(array, isDeep) : [];
}

var flatten_1 = flatten;

/**
 * Gets the property value at `path` of `object`. If the resolved value is
 * `undefined` the `defaultValue` is used in its place.
 *
 * @static
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned if the resolved value is `undefined`.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet_1(object, toPath_1(path), (path + ''));
  return result === undefined ? defaultValue : result;
}

var get_1 = get;

forEachStateToRoot.$inject = ['$state'];
var utils = angular.module('angular-roles.state.utils', [uiRouter]).factory('forEachStateToRoot', forEachStateToRoot);

function forEachStateToRoot($state) {
  return function (startState, func) {
    var parentState = startState;

    while (parentState && parentState.name !== '') {
      startState = parentState;
      func(startState);
      parentState = $state.get('^', startState);
    }
  };
}

PermissionService.$inject = ['$injector', '$state', '$parse', 'forEachStateToRoot'];
var permission = angular.module('angular-roles.permission', [uiRouter, utils.name]).service('Permission', PermissionService);

function PermissionService($injector, $state, $parse, forEachStateToRoot) {
  var _this = this;

  var validations = {};
  var redirects = {};
  var annotations = {};

  /**
   * Defines a role that can be applied to a state to prevent certain users from accessing it.
   *
   * @param  {String}   name        **Required**. Name of the role.
   * @param  {Function} validation  **Required**. Injectable function returning a {Boolean}
   *                                representing whether the current user has the role.
   * @param  {String or redirect    Representation of a redirect state name. Used to
   *          Function}             redirect the user when they fail validation for this role.
   *                                One of:
   *                                1. {String} Redirect state name.
   *                                2. {Function} Injectable, returning one of:
   *                                  a. {String} Redirect state name.
   *                                  b. {Object} Containing `name` and `params` properties.
   *                                     Intended for when the redirect state requires `$stateParams`.
   *                                  c. {Function} Invoked with a {Boolean} representing whether the
   *                                     role was negated (`!`) in the expression in which it was
   *                                     evaluated. Should return a. or b. from above.
   *
   * @return {Object}               Return this for chaining purposes.
   */
  this.defineRole = function (options) {
    var name = options.name,
        validation = options.validation,
        redirect = options.redirect;

    validateRoleDefinitionParams(name, validation, redirect);

    annotations[name] = $injector.annotate(validation);

    Object.defineProperty(validations, name, {
      enumerable: true,
      get: function () {
        var hasRole = void 0;

        try {
          hasRole = !!$injector.invoke(validation, null, $state.$$authLocals);
        } catch (error) {
          hasRole = false;
        }

        return hasRole;
      }
    });

    Object.defineProperty(redirects, name, {
      enumerable: true,
      get: function () {
        if (angular.isFunction(redirect) || Array.isArray(redirect)) {
          return $injector.invoke(redirect);
        } else if (angular.isString(redirect)) {
          return redirect;
        } else {
          return undefined;
        }
      }
    });

    return _this;
  };

  this.getRoleExpression = function (state) {
    var expression = get_1(state, 'data.permissions', '');
    validateExpression(state, expression);
    return expression;
  };

  /**
   * Returns all roles defined for a state.
   *
   * @param  {Object} state State object.
   * @return {[type]}       [description]
   */
  this.getRoles = function (state) {
    var expression = _this.getRoleExpression(state);
    return expression.toString().match(/\w+/g) || [];
  };

  /**
   * Checks whether the current user is authorized to access the given state.
   *
   * @param  {Object} state State object representing the state to authenticate.
   * @return {Boolean}
   */
  this.authorize = function (state) {
    _this.validateRoles(state);
    var expression = _this.getRoleExpression(state);
    return expression === '' ? true : _this.eval(expression);
  };

  /**
   * Evaluates a permissions expression.
   *
   * @param  {String} expression An Angular expression that evaluates roles, referenced by name.
   *                             E.g.: 'user && isLive'
   * @return {Boolean}           The result of the expression.
   */
  this.eval = function (expression) {
    return !!$parse(expression)(validations);
  };

  /**
   * Returns the given state's redirect state (if the user isn't allowed to access the given state).
   * A state's redirect is the redirect property of the first role that fails validation,
   * or `undefined` if no roles fail validation.
   *
   * @param  {Object} state State object to retrieve redirect for.
   * @return {Object}       An {Object} representing the redirect state or `undefined` if there's no redirect.
   */
  this.getRedirect = function (state) {
    var expression = _this.getRoleExpression(state);
    var stateRedirect = void 0;

    if (_this.eval(expression)) {
      // State is authorized so there's no redirect.
      return;
    }

    // Loop through the expression's roles left-to-right, finding the first failing role
    // that has a redirect defined, determining where we redirect to.
    getExpressionRoleNames(expression).some(function (roleName) {
      var isNegated = false;

      if (roleName.indexOf('!') === 0) {
        isNegated = true;
        roleName = roleName.slice(1);
      }

      // If the role wasn't authorized (factoring in negation), look up the redirect.
      if (_this.hasRole(roleName) === isNegated) {
        var roleRedirect = redirects[roleName];
        // If the redirect def returns a function, pass in `isNegated` to allow dynamic
        // redirecting based on whether or not the role was negated.
        stateRedirect = angular.isFunction(roleRedirect) ? roleRedirect(isNegated) : roleRedirect;
      }

      return !!stateRedirect;
    });

    if (angular.isString(stateRedirect)) {
      return {
        name: stateRedirect
      };
    } else {
      return stateRedirect;
    }
  };

  /**
   * Checks whether the current user has the given role(s).
   *
   * @param  {...String} roles List of roles names to check.
   * @return {Boolean}
   */
  this.hasRoles = function () {
    for (var _len = arguments.length, roles = Array(_len), _key = 0; _key < _len; _key++) {
      roles[_key] = arguments[_key];
    }

    return roles.every(function (role) {
      return _this.hasRole(role);
    });
  };

  /**
   * Checks whether the current user has the given role.
   *
   * @param  {String} role Role name to check.
   * @return {Boolean}
   */
  this.hasRole = function (role) {
    return _this.eval(role);
  };

  /**
   * Returns list of roles that the current user has.
   *
   * @return {Array} List of role names.
   */
  this.currentRoles = function () {
    return Object.keys(validations).filter(function (name) {
      return _this.hasRoles(name);
    });
  };

  /**
   * Returns list of the dependencies of all role validation methods for the given state.
   *
   * @param  {String} state State to retrieve dependencies for.
   * @return {Array} List of dependency names.
   */
  this.getRoleDependencies = function (state) {
    return uniq_1(flatten_1(_this.getRoles(state).map(function (role) {
      return annotations[role];
    })));
  };

  /**
   * Returns list of the "resolve dependencies" (=dependencies that are resolved by ui-router)
   * of all role validation methods for the given state. These dependencies are special b/c
   * they cannot be retrieved via $injector. They are a subset of `getRoleDependencies`.
   *
   * @param  {String} state State to retrieve "resolve dependencies" for.
   * @return {Array} List of "resolve dependency" names.
   */
  this.getRoleLocals = function (state) {
    var locals = [];

    forEachStateToRoot(state, function (nextState) {
      if (nextState.resolve) {
        locals.push(Object.keys(nextState.resolve));
      }
    });

    return uniq_1(flatten_1(locals));
  };

  /**
   * Returns a list of the role names in the expression, ordered left-to-right.
   * The role names include negation (`!`), if present.
   *
   * @param  {String} expression Permissions role expression.
   * @return {Array}             List of role names in the expression.
   */
  function getExpressionRoleNames(expression) {
    var regex = /(?:^| |\()(!?\w+)(?:$| |\))/g;
    var matches = [];
    var match = void 0;

    while ((match = regex.exec(expression)) && match[1]) {
      matches.push(match[1]);

      if (regex.lastIndex === match.index) {
        regex.lastIndex++;
      }
    }

    return matches;
  }

  /**
   * Validation functions
   * ----------------------
   */

  this.validateRoles = function (state) {
    var roles = _this.getRoles(state);

    roles.forEach(function (role) {
      if (!validations.hasOwnProperty(role)) {
        throw new Error('Role "' + role + '" is not defined.');
      }
    });
  };

  function validateExpression(state, expression) {
    if (!angular.isString(expression)) {
      throw new Error(state.name + '\'s permissions field must be a string.');
    }
  }

  function validateRoleDefinitionParams(name, validation, redirect) {
    if (!angular.isString(name)) {
      throw new Error('Role name must be a string');
    }

    if (!angular.isFunction(validation) &&
    // May also be an $injector annotated function traveling in an array of injection annotations.
    $injector.annotate(validation).length !== validation.length - 1) {
      throw new Error('Validation function not provided correctly');
    }

    if (redirect && !(angular.isFunction(redirect) || angular.isString(redirect) || Array.isArray(redirect))) {
      throw new Error('Redirect must be a string, a function or an injection annotated array.');
    }
  }
}

StateRun.$inject = ['$rootScope', '$state', '$injector', '$q', 'Permission'];
StateConfig.$inject = ['$provide', '$stateProvider'];
var state = angular.module('angular-roles.state', [uiRouter, permission.name, utils.name]).config(StateConfig).run(StateRun);

var attemptedRedirects = [];

// State-name-to-node lookup for $$auth `path` nodes (see `path` decorator).
var authNodes = {};

function StateConfig($provide, $stateProvider) {
  // Augment $state to provide info on the next state via `next` and `toParams` properties.
  // http://stackoverflow.com/questions/22985988/angular-ui-router-get-state-info-of-tostate-in-resolve/27255909#27255909
  $provide.decorator('$state', ['$delegate', '$rootScope', function ($delegate, $rootScope) {
    $rootScope.$on('$stateChangeStart', function (event, state, params) {
      $delegate.next = state;
      $delegate.toParams = params;
    });
    return $delegate;
  }]);

  // Decorate each state's `path` property with an additional node that handles auth. The `path`
  // property is normally an array of states that link the given state to the state root, used by
  // `ui-router` to determine what to resolve. By appending the "$$auth" node to `path`, we force
  // ui-router to resolve it as a state even though its never registered as one. We can then attach an
  // "$$auth" resolve to this node, allowing authentication rules to accept/reject the state transition.
  // Since the node is not a proper state, it's also re-resolved on revisits, unlike actual states
  // (w/o "reload"), a critical feature of this authentication approach.
  $stateProvider.decorator('path', function (state, parent) {
    var auth = authNodes[state.name] = createAuthNode();

    return parent(state)
    // If we already processed an ancestor of this state, `path` will already contain the
    // ancestor's `$$auth` node. We only want one, state-specific `$$auth` node per path.
    .filter(function (state) {
      return state.name !== '$$auth';
    }).concat(auth);
  });

  // Creates the "$$auth" node that's attached to each state's `path` in the `path` decorator.
  // It's essentially just an object to hang a resolve onto, but needs certain other properties
  // to exist just so that ui-router doesn't choke on it during the state resolution logic.
  function createAuthNode() {
    return {
      name: '$$auth',
      resolve: {},
      self: {},
      params: {
        $$keys: function () {}
      },
      ownParams: {
        $$equals: function () {
          return false;
        }
      }
    };
  }
}

function StateRun($rootScope, $state, $injector, $q, Permission) {
  $rootScope.$on('$stateChangeStart', function (event, toState) {
    var dependencies = Permission.getRoleDependencies(toState);

    // Assign a resolve to perform permissions authentication for this state change.
    // It allows for permission functions to depend on other resolves (e.g. site config).
    authNodes[toState.name].resolve.$$auth = getAuthResolve(toState, dependencies);
  });

  $rootScope.$on('$stateChangeSuccess', function () {
    return attemptedRedirects = [];
  });

  // $stateChangeError
  // ------------------------
  $rootScope.$on('$stateChangeError', function (event, toState) {
    var stateRedirect = Permission.getRedirect(toState);

    if (stateRedirect) {
      if (attemptedRedirects.indexOf(stateRedirect.name) !== -1) {
        throw new Error(['Entered a state redirect infinite loop.', 'Attempted to enter ' + toState.name + ' and was redirected to ' + stateRedirect.name + '.', 'Previously attempted redirects: ' + attemptedRedirects.join(', ') + '.'].join(' '));
      }

      // User may not be authenticated to access the attempted state. Either way, go to its "redirect state".
      if (toState.name !== stateRedirect.name) {
        event.preventDefault();
        attemptedRedirects.push(toState.name);
        $state.go(stateRedirect.name, stateRedirect.params);
      }
    }
  });

  /**
   * Returns a Promise intended to be attached to a state's resolves.
   * It will prevent state transition if the user isn't permitted to access the state.
   *
   * @param  {Object}  toState The state to authenticate.
   * @return {Promise}
   */
  function getAuthResolve(toState, dependencies) {
    // ...args are all the dependencies of the state's validation functions.
    function resolve(Permission) {
      var locals = Permission.getRoleLocals(toState);

      // Attach the state's "locals" (=resolved values) to $state for use by the PermissionsService.
      // This is a bit hacky, but a must in order to run validation functions via $injector.invoke.
      // If a validation function depends on a resolve, that dependency won't be available from $injector.

      for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      $state.$$authLocals = args.map(function (arg, index) {
        return {
          name: dependencies[index],
          value: arg
        };
      }).filter(function (dependency) {
        return locals.indexOf(dependency.name) !== -1;
      }).reduce(function (result, dependency) {
        result[dependency.name] = dependency.value;
        return result;
      }, {});

      // Must use $q here instead of ES6 promises, else we can't unit test (to my knowledge)
      // since calls to $rootScope.$digest only move the test along for $q promises.
      return $q(function (resolve, reject) {
        delete authNodes[toState.name].resolve.$$auth;

        if (!Permission.authorize(toState)) {
          reject('Not authorized to enter state ' + toState.name);
        } else {
          resolve();
        }
      });
    }

    // Annotate the resolve with the dependencies of all role definitions. This ensures that this
    // resolve waits until all of those dependencies have been resolved (if applicable).
    // If we don't do this, we may try to evaluate role definitions too early, before their dependencies
    // have been properly instantiated by the state's resolves.
    resolve.$inject = ['Permission'].concat(dependencies);

    return resolve;
  }
}

/**
 * Modified version of https://github.com/Narzerus/angular-permission/blob/master/src/permission.mdl.js.
 *
 * Not using that library directly yet due to issues with ui-router state handling
 * (always preventDefault, promises): https://github.com/Narzerus/angular-permission/issues/51
 */
var index = angular.module('angular-roles', [permission.name, state.name]);

export default index;
